import { useState, type ReactNode } from 'react';
import { readableKey } from './action-approval-copy';
import type { ActionCopy } from './action-copy';
import { privateActionField } from './action-field-privacy';
import { WorkspaceMarkdown } from '@/workspace/markdown';

type Copy = ActionCopy;
type Entry = readonly [string, unknown];
const longText = /(?:text|content|description|instructions|example|statement|summary)/iu;

function setAtPath(root: Record<string, unknown>, path: string[], value: unknown) {
  const next = structuredClone(root);
  let cursor: Record<string, unknown> = next;
  for (let index = 0; index < path.length - 1; index++) {
    const part = path[index];
    const child = cursor[part];
    cursor[part] = child && typeof child === 'object' ? child : {};
    cursor = cursor[part] as Record<string, unknown>;
  }
  cursor[path[path.length - 1]] = value;
  return next;
}

function flatten(value: unknown, path: string[] = []): Array<{ path: string[]; value: unknown }> {
  if (value === null || ['string', 'number', 'boolean'].includes(typeof value))
    return [{ path, value }];
  if (Array.isArray(value))
    return value.flatMap((item, index) => flatten(item, [...path, String(index)]));
  if (value && typeof value === 'object')
    return Object.entries(value).flatMap(([key, child]) =>
      privateActionField(key) ? [] : flatten(child, [...path, key]),
    );
  return [];
}

function pathLabel(path: string[], copy: Copy) {
  return readableKey(path.filter((part) => !/^\d+$/u.test(part)).at(-1) ?? '', copy);
}

export function ReadableFields({ fields, copy }: { fields: Entry[]; copy: Copy }) {
  const valueView = (value: unknown, key: string): ReactNode => {
    if (value === null || value === undefined) return <>—</>;
    if (typeof value === 'string' && ['markdown', 'instructions'].includes(key))
      return <WorkspaceMarkdown text={value} />;
    if (typeof value === 'object')
      return (
        <ul>
          {Object.entries(value as Record<string, unknown>)
            .filter(([key]) => !privateActionField(key))
            .map(([key, child]) => (
              <li key={key}>
                <strong>{readableKey(key, copy)}</strong>: {valueView(child, key)}
              </li>
            ))}
        </ul>
      );
    return <>{String(value)}</>;
  };
  return (
    <dl className="pc-action-confirmation-details">
      {fields.map(([key, value]) => (
        <div
          key={key}
          className={
            ['payload', 'markdown', 'instructions'].includes(key)
              ? 'pc-action-field-body'
              : undefined
          }
        >
          <dt>{readableKey(key, copy)}</dt>
          <dd>{valueView(value, key)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EditableFields({
  args,
  onChange,
  copy,
}: {
  args: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  copy: Copy;
}) {
  const [expanded, setExpanded] = useState(false);
  const entries = flatten(args).filter(({ path }) => path.length > 0);
  const visible = expanded ? entries : entries.slice(0, 12);
  return (
    <div className="pc-action-confirmation-edit-fields">
      {visible.map(({ path, value }) => {
        const key = path.join('.');
        const label = pathLabel(path, copy);
        const update = (next: unknown) => onChange(setAtPath(args, path, next));
        if (typeof value === 'boolean')
          return (
            <label key={key}>
              {label}
              <input
                type="checkbox"
                aria-label={label}
                checked={value}
                onChange={(event) => update(event.currentTarget.checked)}
              />
            </label>
          );
        if (typeof value === 'number')
          return (
            <label key={key}>
              {label}
              <input
                type="number"
                aria-label={label}
                value={value}
                onChange={(event) => update(Number(event.currentTarget.value))}
              />
            </label>
          );
        return (
          <label key={key}>
            {label}
            {typeof value === 'string' && (value.length > 120 || longText.test(label)) ? (
              <textarea
                aria-label={label}
                value={value}
                rows={Math.min(12, Math.max(3, Math.ceil(value.length / 80)))}
                onChange={(event) => update(event.currentTarget.value)}
              />
            ) : (
              <input
                aria-label={label}
                value={value === null ? '' : String(value)}
                onChange={(event) => update(event.currentTarget.value)}
              />
            )}
          </label>
        );
      })}
      {entries.length > 12 && (
        <button
          type="button"
          className="pc-action-confirmation-show-more"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? copy.review : copy.content}
        </button>
      )}
    </div>
  );
}
